import React, { useEffect, useState } from "react";
import { accountAPI } from "services";
import { Loader, MyPhoneInput, ProfilePhoto } from "components";
import useUniversalStore from "store/universalStore";
import useOffcanvasStore from "store/offcanvasStore";
import { useForm, Controller, useWatch } from "react-hook-form";
import { defaultValues, validateAccountName } from "./config";
import Creatable from "react-select/creatable";
import { creatableStyle } from "assets/themes";
import { getAllTagsFromAccounts, validatePhone } from "utils";
import { defaultTags } from "utils/tags";
import { toast } from "sonner";

const getNameRules = (accountId) => ({
  required: "Name is required",
  maxLength: { value: 50, message: "Name cannot exceed 50 characters" },
  validate: (value) => validateAccountName(value, accountId)
});

const phoneRules = (isShared) => ({
  validate: (value) => {
    if (!value || value === "+91") {
      return isShared ? "Phone is required for shared accounts" : true;
    }
    return validatePhone(value);
  }
});

const tagRules = {
  maxLength: { value: 20, message: "Tag cannot exceed 20 characters" }
};

const EditAccount = () => {
  const { accountId, write, accounts, isSharedAccount, userBasic, user } = useUniversalStore((state) => state);
  const { closeOffcanvas } = useOffcanvasStore();
  const [loading, setLoading] = useState(true);

  const { control, handleSubmit, setValue, trigger, register, formState: { errors, isSubmitting, isValid } } = useForm({
    defaultValues,
    mode: "onBlur"
  });

  const photo = useWatch({ control, name: "photo" });

  const tags = getAllTagsFromAccounts(accounts);
  const mappedTags = tags.map((tag) => ({ value: tag, label: tag?.toUpperCase() }));
  const isShared = useWatch({ control, name: "isShared" });

  useEffect(() => {
    trigger("phone");
  }, [isShared]);

  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        setLoading(true);
        const account = await accountAPI.getAccountById(accountId);
        if (account) {
          setValue("name", account.get("name") || "");
          setValue("phone", account.get("phone") || "");
          setValue("address", account.get("address") || "");
          setValue("details", account.get("details") || "");
          setValue("tag", account.get("tag") || null);
          setValue("photo", {
            url: account.get("photo") || null,
            loading: false
          });
          setValue("isShared", account.get("sharedAcc")?.isShared || false);
          setValue("checkboxSMS", account.get("checkboxSMS") || false);
        }
      } catch (error) {
        toast.error("Failed to load account data");
      } finally {
        setLoading(false);
      }
    };
    fetchAccountData();
  }, [accountId]);

  const onSubmit = async (data) => {
    try {
      await accountAPI.updateAccount(
        accountId,
        data.name,
        data.phone,
        data.address,
        data.details,
        data.tag?.value ?? data.tag ?? null,
        data.photo.url,
        data.isShared,
        userBasic?.business?.name || user?.get("username"),
        data.checkboxSMS
      );

      write({ accounts: await accountAPI.getAccounts() });
      closeOffcanvas();
      toast.success("Account updated successfully");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const inputClass = (name) => `form-control border-0 ${errors[name] ? "is-invalid" : ""}`;

  const getTagValue = (value) => {
    if (!value) return null;
    if (typeof value === "string") return { value, label: value };
    return value;
  };

  if (loading) return <Loader />;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <fieldset disabled={isSharedAccount}>

        <div className="d-flex flex-wrap align-items-center">
          <div className="w-50">
            <ProfilePhoto
              key="edit-profile-photo"
              url={photo?.url}
              loading={photo?.loading}
              updateField={(field, data) => setValue(field, data)}
            />
          </div>
          <div className="w-50 d-flex flex-column flex-wrap flex-grow-1 text-end">
            <div className="form-control p-0"> {/* removed form-control to avoid extra borders */}
              <input
                {...register("name", getNameRules(accountId))}
                type="text"
                placeholder="Account Name"
                className={`
                  ${inputClass("name")}
                  border-0
                  border-bottom
                  border-1
                  border-danger
                  rounded-0
                  px-2
                  fw-bold
                  text-end
                `.replace(/\s+/g, ' ')}
                style={{ boxShadow: "none" }}
                disabled={isSubmitting}
              />
            </div>
            {errors.name && <div className="invalid-feedback d-block">{errors.name.message}</div>}

            <div className="d-flex justify-content-end align-items-center mt-2 text-end">
              <label className="form-check-label mb-0 me-2" htmlFor="checkboxSMS" style={{ marginBottom: 0 }}>
                <small>Notify via SMS</small>
              </label>
              <div className="form-check form-switch cursor-pointer">
                <input
                  className="form-check-input cursor-pointer"
                  type="checkbox"
                  id="checkboxSMS"
                  {...register("checkboxSMS")}
                  disabled={isSubmitting}
                  style={{ verticalAlign: 'middle' }}
                />
              </div>
            </div>

            {isSharedAccount && (
              <div className="mt-2">
                <i className="bi bi-dot text-danger fs-5 align-middle"></i>
                <span className="fs-6 fw-light fw-italic text-danger">Can't edit shared account</span>
              </div>
            )}
          </div>
        </div>

        <hr />
        <div className="d-flex flex-wrap align-items-center">
          <span className="w-25">Phone</span>
          <span className="w-50 flex-grow-1"></span>
          <div className="w-25 d-flex flex-column align-items-end">
              <Controller
                name="phone"
                control={control}
                rules={phoneRules(isShared)}
                render={({ field }) => (
                  <MyPhoneInput
                    className="text-end"
                    field={field}
                    error={errors.phone}
                    isSubmitting={isSubmitting || isSharedAccount}
                  />
                )}
              />
              {errors.phone && <div className="invalid-feedback d-block text-end">{errors.phone.message}</div>}
          </div>
        </div>
        <hr />
        <div className="d-flex flex-wrap align-items-center">
          <span className="w-50">Tag</span>
          <div className="flex-grow-1">
            <Controller
              name="tag"
              control={control}
              rules={tagRules}
              render={({ field: { value, onChange } }) => (
                <Creatable
                  isClearable
                  options={mappedTags}
                  value={getTagValue(value)}
                  onChange={onChange}
                  placeholder="Select or create"
                  classNamePrefix="rs"
                  classNames={creatableStyle}
                  className={`text-end ${errors.tag ? "is-invalid" : value ? "is-valid" : ""}`}
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary: "#d83047",
                      primary25: "#f47788",
                      primary50: "#d83047"
                    }
                  })}
                  styles={{
                    control: (base) => ({
                      ...base,
                      border: "none",
                      boxShadow: "none"
                    })
                  }}
                  isDisabled={isSubmitting || isSharedAccount}
                />
              )}
            />
            {errors.tag && <div className="invalid-feedback d-block">{errors.tag.message}</div>}
          </div>
        </div>
 
        <hr />
        <div className="d-flex flex-wrap align-items-center">
          <span className="w-25">Address</span>
          <div className="flex-grow-1">
            <input
              {...register("address")}
              type="text"
              placeholder="Address"
              className={`text-end ${inputClass("address")} `}
              disabled={isSubmitting}
            />
          </div>
        </div>
        <hr />
        <div className="d-flex flex-wrap align-items-center">
          <span className="w-25">Other details</span>
          <div className="flex-grow-1">
            <div className="form-control border-0 p-0">
              <textarea
                {...register("details")}
                className={`text-end ${inputClass("details")} `}
                rows={2}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>
        <hr />

        {!isSharedAccount && (
          <div className="px-3 position-absolute bottom-0 start-0 end-0 mb-2 mb-md-3">
            <button
              type="submit"
              className="btn btn-app w-100 py-2 py-md-3 fs-5 fw-bold text-uppercase"
              disabled={isSubmitting || photo?.loading || !isValid}
            >
              {photo?.loading ? (
                <span className="spinner-border spinner-border-sm me-2" />
              ) : isSubmitting ? "Updating..." : "Update"}
            </button>
          </div>
        )}
      </fieldset>
    </form>
  );
};

export default EditAccount;
